// eslint-disable-next-line import/no-unresolved
import swiperCss from '!!raw-loader!swiper/css/bundle'
import Feet from '@bluheadless/ui/src/particles/feet'
import Head from '@bluheadless/ui/src/particles/head'
import Manifest from '@bluheadless/ui/src/particles/manifest'
import RichSnippetWebsite from '@bluheadless/ui/src/particles/rich-snippets/website'
import RichSnippetOrganization from '@bluheadless/ui/src/particles/rich-snippets/organization'
import RouteProgress from '@bluheadless/ui/src/particles/route-progress'
import PageLoader from '@bluheadless/ui/src/particles/page-loader'
import { CacheProvider } from '@emotion/react'
import { CssBaseline, GlobalStyles, NoSsr } from '@mui/material'
import { ThemeProvider } from '@mui/system'
import { createTheme } from '@mui/material/styles' // keep @mui/material/styles to use default theme of @mui/material
import mediaQuery from 'css-mediaquery'
import { getCmsBlocks, getMenus } from 'data-api'
import NextApp from 'next/app'
import { useRef, useCallback, useMemo } from 'react'
import TrackingsGlobal from './components/trackings/global'
import Vary from './components/vary/vary'
import mergeWith from 'lodash/mergeWith'
import ErrorBoundary from './error-boundary'
import { useDetectScrollWidth } from './hooks/useDetectScrollWidth'
import { useErrorPages } from './hooks/useErrorPages'
import Layout from '@bluheadless/ui/src/layouts/layout'
import { useViewportUnit } from './hooks/useViewportUnit'
import { useScrollRestorationManual } from './hooks/useScrollRestorationManual'
import EssentialProviders from './providers/essentials'
import Providers from './providers/providers'
import { redirectToLogin as restrictionRedirectToLogin } from './restriction'
import nextDataFetchCookiesProxy from './next-data-fetch-cookies-proxy'
import { useConstructor } from './hooks/useConstructor'
import SeoMenu from '@bluheadless/ui/src/particles/seo/menu'
import nextRouterHistoryProxy from './next-router-history-proxy'
import CoreWebVitalsGlobal from './core-web-vitals/global'
import { useRestrictions } from './hooks/useRestrictions'
import { composeAndSerializeSwrKey } from '@bluheadless/ui-logic/src/stale-while-revalidate/cache-key'
import { HEADER_NAME_DETECTED_DEVICE } from './constants'

/**
 * External styles
 * To include global styles before Emotion's styles, import it with raw-loader and put in globalStyles
 * pointer-events: none is related to this issue https://github.com/mui/material-ui/issues/32286#issuecomment-1287951109 - FRANCHI-662
 */
const defaultGlobalStyles = `
${swiperCss}
div.MuiModal-root:has(> div.MuiBackdrop-root[style*="opacity: 0"]) {
    pointer-events: none;
  }
`

const Restrictions = () => {
	useRestrictions()
	return null
}

const ViewportData = () => {
	useDetectScrollWidth()
	useViewportUnit()
	return null
}

const BluHeadlessApp = ({
	Component,
	routerPathname,
	routerAsPath,
	pageProps: {
		__bh__currentStoreCode,
		__bh__currentCurrencyCode,
		__bh__config,
		__bh__defaultTranslations,
		__bh__translations,
		__bh__userAuthenticated,
		__bh__salesAgentAuthenticated,
		__bh__userGroup,
		__bh__salesAgentGroup,
		swrConfig,
		__bh__swrConfig,
		...pageProps
	},
	userAgent,
	emotionCache,
	theme,
	globalStyles = defaultGlobalStyles,
}) => {
	useScrollRestorationManual()
	useConstructor(() => {
		nextDataFetchCookiesProxy()
		nextRouterHistoryProxy()
	})
	// First time _app render __bh__currentStoreCode, __bh__currentCurrencyCode and __bh__config has values
	// During route navigation to static-page app pageProps are empty
	// We need to use the latest received values
	const storeRef = useRef(__bh__currentStoreCode)
	const currencyRef = useRef(__bh__currentCurrencyCode)
	const configRef = useRef(__bh__config)
	const defaultTranslationsRef = useRef(__bh__defaultTranslations)
	const translationsRef = useRef(__bh__translations)
	const userAuthenticatedRef = useRef(__bh__userAuthenticated)
	const salesAgentAuthenticatedRef = useRef(__bh__salesAgentAuthenticated)
	const userGroupRef = useRef(parseInt(__bh__userGroup))
	const salesAgentGroupRef = useRef(parseInt(__bh__salesAgentGroup))
	const swrConfigRef = useRef({})

	if (__bh__swrConfig) {
		// This is needed, otherwise getServerSideProps from page
		// will override the default swrConfig coming from getInitialProps.
		//! The __bh__swrConfig key needs to be different!
		swrConfigRef.current = mergeWith({}, swrConfigRef.current, __bh__swrConfig, (objValue, srcValue) => {
			// always override array value
			if (Array.isArray(objValue)) {
				return srcValue
			}
		})
	}

	if (swrConfig) {
		// TODO: verify if all swrConfig keys are included in swrConfigRef.current
		swrConfigRef.current = mergeWith({}, swrConfigRef.current, swrConfig, (objValue, srcValue) => {
			// always override array value
			if (Array.isArray(objValue)) {
				return srcValue
			}
		})
	}

	// UPDATE REFS FROM SERVER-SIDE
	// Need to update refs before rendering. eg: from not-store page client-side navigation to store page server-side

	if (__bh__currentStoreCode && __bh__currentStoreCode !== storeRef.current) {
		storeRef.current = __bh__currentStoreCode
	}
	if (__bh__currentCurrencyCode && __bh__currentCurrencyCode !== currencyRef.current) {
		currencyRef.current = __bh__currentCurrencyCode
	}
	if (__bh__config && __bh__currentStoreCode && __bh__currentStoreCode !== storeRef.current) {
		configRef.current = __bh__config
	}
	if (__bh__defaultTranslations && __bh__currentStoreCode && __bh__currentStoreCode !== storeRef.current) {
		defaultTranslationsRef.current = __bh__defaultTranslations
	}
	if (__bh__translations && __bh__currentStoreCode && __bh__currentStoreCode !== storeRef.current) {
		translationsRef.current = __bh__translations
	}

	if (__bh__userAuthenticated !== undefined && __bh__userAuthenticated !== userAuthenticatedRef.current) {
		userAuthenticatedRef.current = __bh__userAuthenticated
	}

	if (
		__bh__salesAgentAuthenticated !== undefined &&
		__bh__salesAgentAuthenticated !== salesAgentAuthenticatedRef.current
	) {
		salesAgentAuthenticatedRef.current = __bh__salesAgentAuthenticated
	}

	if (__bh__userGroup !== undefined && parseInt(__bh__userGroup) !== userGroupRef.current) {
		userGroupRef.current = parseInt(__bh__userGroup)
	}

	if (__bh__salesAgentGroup !== undefined && parseInt(__bh__salesAgentGroup) !== salesAgentGroupRef.current) {
		salesAgentGroupRef.current = parseInt(__bh__salesAgentGroup)
	}
	// END UPDATE REFS FROM SERVER-SIDE

	const ssrMatchMedia = useCallback(
		(query) => ({
			matches:
				userAgent === 'mobile'
					? mediaQuery.match(query, {
							width: `${theme.breakpoints.values[configRef?.current?.layout?.breakpoints?.mobile || 'xs']}px`,
					  })
					: mediaQuery.match(query, {
							width: `${theme.breakpoints.values[configRef?.current?.layout?.breakpoints?.desktop || 'lg']}px`,
					  }),
		}),
		[userAgent, theme.breakpoints.values]
	)

	const projectTheme = useMemo(
		() =>
			createTheme(theme, {
				components: {
					MuiUseMediaQuery: {
						defaultProps: {
							ssrMatchMedia,
						},
					},
				},
			}),
		[theme, ssrMatchMedia]
	)

	const { isError, isMaintenance } = useErrorPages({ pathname: routerPathname })

	const restrictionEnabled =
		configRef.current?.restriction?.enabled && !userAuthenticatedRef.current && !salesAgentAuthenticatedRef.current

	return (
		<CacheProvider value={emotionCache}>
			<ThemeProvider theme={projectTheme}>
				<GlobalStyles styles={globalStyles} />
				{/* Including ViewportData into NoSsr due to https://reactjs.org/link/uselayouteffect-ssr */}
				<NoSsr>
					<ViewportData />
				</NoSsr>
				{/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
				<CssBaseline />
				<RouteProgress />

				{isError || isMaintenance || !storeRef.current ? (
					<EssentialProviders
						config={configRef.current}
						store={storeRef.current}
						currency={currencyRef.current}
						defaultTranslations={defaultTranslationsRef.current}
						translations={translationsRef.current}
						userAuthenticated={userAuthenticatedRef.current}
						salesAgentAuthenticated={salesAgentAuthenticatedRef.current}
						userGroup={userGroupRef.current}
						salesAgentGroup={salesAgentGroupRef.current}
						swrConfigProps={{ value: swrConfigRef.current }}
						pageProps={pageProps}
					>
						<ErrorBoundary resetKey={routerAsPath}>
							<Head />
							<CoreWebVitalsGlobal />
							<TrackingsGlobal />
							<NoSsr>
								<Vary />
							</NoSsr>
							<PageLoader />
							<Layout
								Component={Component}
								restrictionEnabled={restrictionEnabled}
								isError={isError}
								isMaintenance={isMaintenance}
							>
								<Component {...pageProps} />
							</Layout>
							<Feet />
						</ErrorBoundary>
					</EssentialProviders>
				) : (
					<Providers
						config={configRef.current}
						store={storeRef.current}
						currency={currencyRef.current}
						defaultTranslations={defaultTranslationsRef.current}
						translations={translationsRef.current}
						userAuthenticated={userAuthenticatedRef.current}
						salesAgentAuthenticated={salesAgentAuthenticatedRef.current}
						userGroup={userGroupRef.current}
						salesAgentGroup={salesAgentGroupRef.current}
						swrConfigProps={{ value: swrConfigRef.current }}
						pageProps={pageProps}
					>
						<ErrorBoundary resetKey={routerAsPath}>
							<Restrictions />
							<Head />
							<Manifest />
							<RichSnippetWebsite />
							<RichSnippetOrganization />
							<CoreWebVitalsGlobal />
							<TrackingsGlobal />
							<NoSsr>
								<Vary />
							</NoSsr>
							<PageLoader />
							<SeoMenu />
							<Layout
								Component={Component}
								restrictionEnabled={restrictionEnabled}
								isError={isError}
								isMaintenance={isMaintenance}
							>
								<Component {...pageProps} />
							</Layout>
							<Feet />
						</ErrorBoundary>
					</Providers>
				)}
			</ThemeProvider>
		</CacheProvider>
	)
}

const getCacheKey = (namespace, key, req) =>
	`${namespace}_${req?.__bh__currentStoreCode}_${req?.__bh__userGroup}_${key}`

const userAgentRegex = /iPhone|iPod|Android|BlackBerry|Opera Mini|Mobile|Dolphin|Silk/

const getUserAgent = (req) =>
	req?.headers?.[HEADER_NAME_DETECTED_DEVICE] ??
	(userAgentRegex.test(req?.headers?.['user-agent']) || req?.headers?.['sec-ch-ua-mobile']?.includes('1')
		? 'mobile'
		: 'desktop')

BluHeadlessApp.getInitialProps = async (appContext) => {
	const {
		ctx: { req, res, asPath, pathname, err }, // req could be undefined during route change to static page
	} = appContext

	const initialProps = await NextApp.getInitialProps(appContext)

	const essentialPageProps = {
		__bh__userAuthenticated: req?.__bh__userAuthenticated,
		__bh__salesAgentAuthenticated: req?.__bh__salesAgentAuthenticated,
		__bh__userGroup: req?.__bh__userGroup,
		__bh__salesAgentGroup: req?.__bh__salesAgentGroup,
	}

	const userAgent = getUserAgent(req)

	if (/^\/_next\/data/.test(req?.path)) {
		return {
			...initialProps,
			routerPathname: pathname,
			routerAsPath: asPath,
			pageProps: {
				...initialProps.pageProps,
				...essentialPageProps,
			},
			userAgent,
		}
	}

	const store = req?.__bh__config?.availableStores.find(({ code }) => code === req?.__bh__currentStoreCode)

	const restrictionRedirect = restrictionRedirectToLogin({
		isCustomerLoggedIn: req?.__bh__userAuthenticated,
		isSalesAgentLoggedIn: req?.__bh__salesAgentAuthenticated,
		store,
		restrictionConfig: req?.__bh__config?.restriction,
		pathname,
		asPath,
	})

	if (restrictionRedirect) {
		const { permanent, destination } = restrictionRedirect
		res.writeHead(permanent ? 301 : 302, { Location: destination })
		res.write(destination)
		res.end()
		return {}
	}

	const appCache = req?.__bh__cacheInstance

	const swrConfig = new Map()
	const appCacheKeys = []
	const promises = []

	const canGetMenu = !err && req?.__bh__currentStoreCode && req?.__bh__client && req?.__bh__config?.menus?.top?.code

	if (canGetMenu) {
		const mainMenuCacheKey = getCacheKey('MENU', req?.__bh__config?.menus?.top?.code, req)
		appCacheKeys.push(mainMenuCacheKey)
		const mainMenuSwrKey = composeAndSerializeSwrKey({
			method: getMenus,
			client: req?.__bh__client,
			args: { code: req?.__bh__config?.menus?.top?.code },
		})

		if (appCache.has(mainMenuCacheKey)) {
			swrConfig.set(mainMenuSwrKey, appCache.get(mainMenuCacheKey))
		} else {
			swrConfig.set(mainMenuSwrKey, undefined)
			promises.push(
				getMenus({
					client: req?.__bh__client,
					code: req?.__bh__config?.menus?.top?.code,
				})
			)
		}
	}

	const canGetFooterMenu =
		!err && req?.__bh__currentStoreCode && req?.__bh__client && req?.__bh__config?.menus?.bottom?.code

	const footerMenuCacheKey = getCacheKey('MENU', req?.__bh__config?.menus?.bottom?.code, req)

	if (canGetFooterMenu) {
		appCacheKeys.push(footerMenuCacheKey)
		const footerMenuSwrKey = composeAndSerializeSwrKey({
			method: getMenus,
			client: req?.__bh__client,
			args: { code: req?.__bh__config?.menus?.bottom?.code },
		})

		if (appCache.has(footerMenuCacheKey)) {
			swrConfig.set(footerMenuSwrKey, appCache.get(footerMenuCacheKey))
		} else {
			swrConfig.set(footerMenuSwrKey, undefined)
			promises.push(
				getMenus({
					client: req?.__bh__client,
					code: req?.__bh__config?.menus?.bottom?.code,
				})
			)
		}
	}

	const canGetNotifyMessageBlocks =
		!err &&
		req?.__bh__currentStoreCode &&
		req?.__bh__client &&
		req?.__bh__config?.notifyMessage?.enabled &&
		(req?.__bh__config?.notifyMessage?.cmsBlockIdentifier ||
			req?.__bh__config?.notifyMessage?.cmsBlockIdentifierAdditionalContent)

	if (canGetNotifyMessageBlocks) {
		const notifyMessageBlocksCacheKey = getCacheKey(
			'NOTIFY_MESSAGE_BLOCKS',
			`${req?.__bh__config?.notifyMessage?.cmsBlockIdentifier}_${req?.__bh__config?.notifyMessage?.cmsBlockIdentifierAdditionalContent}`,
			req
		)
		appCacheKeys.push(notifyMessageBlocksCacheKey)
		const notifyMessageBlocksWhere = {
			idx: {
				inq: [
					req?.__bh__config?.notifyMessage?.cmsBlockIdentifier,
					req?.__bh__config?.notifyMessage?.cmsBlockIdentifierAdditionalContent,
				],
			},
		}
		const notifyMessageBlocksSwrKey = composeAndSerializeSwrKey({
			method: getCmsBlocks,
			client: req?.__bh__client,
			args: { where: notifyMessageBlocksWhere },
		})

		if (appCache.has(notifyMessageBlocksCacheKey)) {
			swrConfig.set(notifyMessageBlocksSwrKey, appCache.get(notifyMessageBlocksCacheKey))
		} else {
			swrConfig.set(notifyMessageBlocksSwrKey, undefined)
			promises.push(
				getCmsBlocks({
					client: req?.__bh__client,
					where: notifyMessageBlocksWhere,
					fields: {
						idx: true,
						content: true,
					},
				})
			)
		}
	}

	if (promises.length) {
		const results = await Promise.all(promises)
		results.forEach((result, index) => {
			const cacheKey = Array.from(swrConfig.keys())[index]
			appCache.set(appCacheKeys[index], result?.data)
			swrConfig.set(cacheKey, result?.data)
		})
	}

	const __bh__config = Object.assign({}, req?.__bh__config)
	// reduce payload removing useless and sensible data
	delete __bh__config.appleDeveloperMerchantIdDomainAssociation
	delete __bh__config.googleRecaptcha?.secretKey

	return {
		...initialProps,
		routerPathname: pathname,
		routerAsPath: asPath,
		pageProps: {
			...initialProps.pageProps,
			...essentialPageProps,
			__bh__currentStoreCode: req?.__bh__currentStoreCode,
			__bh__currentCurrencyCode: req?.__bh__currentCurrencyCode,
			__bh__config,
			__bh__currentLocaleCode: req?.__bh__currentLocaleCode,
			__bh__defaultTranslations: req?.__bh__defaultTranslations ?? {},
			__bh__translations: req?.__bh__translations ?? {},
			__bh__swrConfig: {
				fallback: {
					...Object.fromEntries(swrConfig),
				},
			},
		},
		userAgent,
	}
}

export default BluHeadlessApp
